import {createApp} from 'vue';
import {ZiggyVue} from 'ziggy-js';
import {vueStore} from './store/vue-store';
import {vueComponents} from './vue-components';
import vueRouter from './vue-router';
import bootstrapTooltip from './directives/bootstrapTooltip';
import {hideGlobalLoadingOverlay} from "../utils/loadingOverlay";
import {attachVueErrorHandler} from "../utils/errorReporting";
import {handleChunkLoadError, clearChunkReloadFlag} from "../utils/chunkErrorHandler";

function markVueReady() {
    document.body?.classList.add('vue-ready');
    hideGlobalLoadingOverlay();
}

/**
 * Mounts a Vue component to the DOM element.
 * @param componentConfig
 * @param {Element} element - The DOM element to mount the component to.
 */
function mountComponent(componentConfig, element) {
    try {
        // Extract all props from `data-` attributes and convert JSON strings to objects
        const props = {};
        for (const { name, value } of element.attributes) {
            if (name.startsWith("data-")) {
                const propName = name.replace("data-", "").replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
                try {
                    props[propName] = JSON.parse(value);
                } catch (e) {
                    props[propName] = value; // If it's not JSON, store as a string
                }
            }
        }

        // console.log(`Mounting "${element.id}" with props:`, props);

        const app = createApp(componentConfig.component, props);
        attachVueErrorHandler(app, {componentName: componentConfig.componentName});
        app.directive('tooltip', bootstrapTooltip);

        app.use(ZiggyVue);
        app.use(vueStore);

        if (componentConfig.hasRouter) {
            app.use(vueRouter);
        }

        app.mount(element);

    } catch (error) {
        console.error(`Error mounting component: ${element.id}`, error);
    }
}



/**
 * Dynamically registers a Vuex module if it's provided.
 * @param {String} moduleName - The name of the module (componentName).
 * @param {Object} vuexModule - The Vuex module to register.
 */
function registerVuexModule(moduleName, vuexModule) {
    if (vuexModule) {
        try {
            vueStore.registerModule(moduleName, vuexModule);
            console.log(`Vuex module "${moduleName}" successfully registered.`);
        } catch (error) {
            console.error(`Error registering Vuex module: ${moduleName}`, error);
        }
    }
}

/**
 * Initializes and mounts Vue components dynamically based on the DOM.
 */
async function initializeComponents() {
    markVueReady();
    for (const componentName of Object.keys(vueComponents)) {
        const element = document.getElementById(componentName);

        if (element) {
            const componentConfig = vueComponents[componentName];
            const { component: componentLoader } = componentConfig;

            try {
                // Dynamically import the component
                const component = (await componentLoader()).default;

                // Register Vuex module if provided
                registerVuexModule(componentName, componentConfig?.vuexModule);

                // Mount the component with all its properties
                mountComponent({ ...componentConfig, component, componentName }, element);
                // console.log(`Component "${componentName}" mounted successfully.`);
            } catch (error) {
                if (!handleChunkLoadError(error)) {
                    console.error(`Error loading component "${componentName}":`, error);
                }
            }
        }
    }
}


// Initialize components when the page loads
initializeComponents()
    .then(() => {
        clearChunkReloadFlag();
        console.log("App is running...");
    })
    .catch((error) => {
        console.error("Error initializing components:", error);
    })
